// File: src/components/Invoices/InvoiceList.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Eye, Download, Loader2, UploadCloud, ShieldCheck, History, List } from "lucide-react";
import { Link } from "react-router-dom";
import api from "@/lib/axios"; // ✅ default import
import {normalizeInvoiceHistory} from '@/lib/invoiceReviewAccess';
import {useAuth} from '@/context/AuthProvider';
import {OPERATIONAL_CONTEXT_EVENT} from '@/lib/procurementOperationalContext';

const toStr = (v) => (v == null ? "" : String(v));

export default function InvoiceList() {
  const {user}=useAuth();
  const [revision,setRevision]=useState(0);
  const requestKey=JSON.stringify([user?.id,user?.company_id,user?.unit_id,user?.role,user?.is_platform_admin,revision]);
  const keyRef=useRef(requestKey);keyRef.current=requestKey;
  const activeRequest=useRef(null),sequence=useRef(0);
  const [state,setState]=useState({key:'',rows:[],loading:true,error:''});
  const rows=state.key===requestKey?state.rows:[];
  const loading=state.key!==requestKey||state.loading;
  const loadError=state.key===requestKey?state.error:'';

  // Build a safe absolute URL for files whether backend returns absolute or relative paths.
  const toFileUrl = (inv) => {
    const raw =
      inv?.file_url ||
      inv?.fileUrl ||
      (inv?.filename ? `/uploads/invoices/${inv.filename}` : "") ||
      (inv?.file_name ? `/uploads/invoices/${inv.file_name}` : "");

    if (!raw) return "";
    if (/^https?:\/\//i.test(raw)) return raw;

    try {
      const base = toStr(api?.defaults?.baseURL || "");
      const apiOrigin = base ? new URL(base, window.location.origin).origin : window.location.origin;
      const path = raw.startsWith("/") ? raw : `/${raw}`;
      return new URL(path, apiOrigin).toString();
    } catch {
      return raw;
    }
  };

  const fetchInvoices = useCallback(async () => {
    activeRequest.current?.abort();
    const controller=new AbortController();activeRequest.current=controller;
    const serial=++sequence.current;
    setState({key:requestKey,rows:[],loading:true,error:''});
    try{
      const {data}=await api.get('/invoice/history',{signal:controller.signal});
      const loaded=normalizeInvoiceHistory(data);
      if(!controller.signal.aborted&&serial===sequence.current&&keyRef.current===requestKey)
        setState({key:requestKey,rows:loaded,loading:false,error:''});
    }catch(error){
      if(!controller.signal.aborted&&serial===sequence.current&&keyRef.current===requestKey)
        setState({key:requestKey,rows:[],loading:false,error:[401,403].includes(error?.response?.status)?'Invoice history is unavailable in your current access.':'Invoice history could not be loaded. Please retry.'});
    }
  },[requestKey]);

  useEffect(() => {
    fetchInvoices();
    return () => activeRequest.current?.abort();
  }, [fetchInvoices]);
  useEffect(()=>{
    const changed=()=>{activeRequest.current?.abort();setRevision(value=>value+1);};
    window.addEventListener(OPERATIONAL_CONTEXT_EVENT,changed);
    return ()=>window.removeEventListener(OPERATIONAL_CONTEXT_EVENT,changed);
  },[]);

  // ✅ Only show uploaded invoices in this table (prevents tons of “—” rows)
  const uploaded = useMemo(() => {
    return rows.filter((inv) => {
      const hasFile =
        Boolean(inv?.file_url || inv?.fileUrl || inv?.filename || inv?.file_name);
      return hasFile;
    });
  }, [rows]);

  return (
    <div className="min-h-screen p-6 text-white bg-[#0b0f19]">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight">📄 Invoice Management</h1>
          <p className="text-white/60 mt-1">
            Central dashboard for invoices (upload, approvals, history, and files).
          </p>
        </div>

        <button
          type="button"
          onClick={fetchInvoices}
          className="px-3 py-2 rounded bg-white/10 hover:bg-white/15 border border-white/10 text-sm"
        >
          Refresh
        </button>
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 mb-6">
        <Link
          to="/invoice/upload"
          className="rounded-2xl border border-white/10 bg-white/5 hover:bg-white/10 transition p-4 flex items-center gap-3"
        >
          <UploadCloud className="opacity-90" />
          <div>
            <div className="font-semibold">Upload Invoice</div>
            <div className="text-xs text-white/60">Add PDF + metadata</div>
          </div>
        </Link>

        <Link
          to="/invoice/approval"
          className="rounded-2xl border border-white/10 bg-white/5 hover:bg-white/10 transition p-4 flex items-center gap-3"
        >
          <ShieldCheck className="opacity-90" />
          <div>
            <div className="font-semibold">Approvals</div>
            <div className="text-xs text-white/60">Approve / reject invoices</div>
          </div>
        </Link>

        <Link
          to="/invoice/history"
          className="rounded-2xl border border-white/10 bg-white/5 hover:bg-white/10 transition p-4 flex items-center gap-3"
        >
          <History className="opacity-90" />
          <div>
            <div className="font-semibold">History</div>
            <div className="text-xs text-white/60">Paid / approved timeline</div>
          </div>
        </Link>

        <Link
          to="/invoice"
          className="rounded-2xl border border-white/10 bg-white/5 hover:bg-white/10 transition p-4 flex items-center gap-3"
        >
          <List className="opacity-90" />
          <div>
            <div className="font-semibold">All Invoices</div>
            <div className="text-xs text-white/60">Full list / details</div>
          </div>
        </Link>
      </div>

      {/* Uploaded invoices table */}
      <div className="bg-gradient-to-br from-white/5 to-white/0 border border-white/10 p-6 rounded-3xl shadow-xl backdrop-blur-sm">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">📁 Uploaded Invoices</h2>
          <div className="text-xs text-white/60">
            Showing {uploaded.length} file(s)
          </div>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-blue-300">
            <Loader2 className="animate-spin" />
            Loading invoices...
          </div>
        ) : loadError ? <p role="alert" className="text-rose-300">{loadError}</p> : uploaded.length === 0 ? (
          <p className="text-yellow-400">⚠️ No invoice files uploaded yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm text-white">
              <thead className="bg-black/30 text-white/90">
                <tr>
                  <th className="px-4 py-3 text-left">Filename</th>
                  <th className="px-4 py-3 text-left">Uploaded By</th>
                  <th className="px-4 py-3 text-left">Date</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {uploaded.map((inv) => {
                  const fileUrl = toFileUrl(inv);
                  const key = inv.invoice_id ?? inv.id ?? inv.filename ?? inv.file_name ?? `${Math.random()}`;

                  return (
                    <tr key={key} className="hover:bg-white/5 transition-all duration-200">
                      <td className="px-4 py-3 font-medium">{inv.filename || inv.file_name || "—"}</td>
                      <td className="px-4 py-3">{inv.uploaded_by || "—"}</td>
                      <td className="px-4 py-3">
                        {inv.uploaded_at
                          ? new Date(inv.uploaded_at).toLocaleDateString()
                          : inv.created_at
                            ? new Date(inv.created_at).toLocaleDateString()
                            : "—"}
                      </td>
                      <td className="px-4 py-3 text-right space-x-3">
                        {fileUrl ? (
                          <>
                            <a
                              href={fileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center text-sky-400 hover:text-sky-300 transition"
                            >
                              <Eye size={16} className="mr-1" />
                              View
                            </a>
                            <a
                              href={fileUrl}
                              download
                              className="inline-flex items-center text-green-400 hover:text-green-300 transition"
                            >
                              <Download size={16} className="mr-1" />
                              Download
                            </a>
                          </>
                        ) : (
                          <span className="text-white/50">No file</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
